import { _ as _defineProperty } from '../defineProperty-2d1aa32d.js';
import { _ as _slicedToArray } from '../slicedToArray-a6a95674.js';
import { useState, useEffect, useMemo, useCallback } from 'react';
import getMobileOperatingSystem from '@picsart/rc/getMobileOperatingSystem';
import { useTranslator } from '@picsart/rc/services/localization';
import { GOLD_LOGO, DEFAULT_SCOPE_ID, DEFAULT_TEAM_SCOPE_ID, STORE_URL, PLUS_LOGO, PRO_LOGO, DEFAULT_PLUS_TIER_SCOPE_ID, DEFAULT_PRO_TIER_SCOPE_ID } from '../constants/index.js';
import { fetchSubscriptionPlans } from '../subscriptionsService/index.js';
import { fetchFeaturesAvailability } from '../featuresService/index.js';
import { S as Switch } from '../Switch-6eb87a1f.js';
import { Skeleton } from '@picsart/design-system/components/Skeleton';
import { c as classnames } from '../index-4009c965.js';
import { createUseStyles } from 'react-jss';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { mediaQuery, BreakPoints } from '../helpers/index.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { BackgroundColors, PrimaryColors, SecondaryColors, TextColors, PositiveColors } from '@picsart/design-system/foundation/colors';
import { ButtonVariants, ButtonSkinAbsoluteBlack, ButtonSkinPrimary, ButtonSkinSecondary, Button, ButtonElementTypes } from '@picsart/design-system/components/Button';
import { toFixedPrice } from '../currencyService/index.js';
import { _ as _toConsumableArray } from '../toConsumableArray-5dfc8238.js';
import { _ as _objectWithoutProperties } from '../objectWithoutProperties-2f896e18.js';
import { FontSizes } from '@picsart/design-system/foundation/typography';
import { FontWeights } from '@picsart/design-system/foundation/fonts';

var useStyles$1 = createUseStyles({
  container: _defineProperty({
    gap: Spacings.s8,
    display: 'flex',
    width: '100%'
  }, mediaQuery(BreakPoints.Large), {
    flexDirection: 'column',
    gap: Spacings.s24
  }),
  "switch": {
    marginBottom: Spacings.s16
  },
  card: _defineProperty({
    flexBasis: '100%',
    '&:first-child': {
      borderBottomLeftRadius: CornerRadius.r8,
      borderTopLeftRadius: CornerRadius.r8
    },
    '&:last-child': {
      borderBottomRightRadius: CornerRadius.r8,
      borderTopRightRadius: CornerRadius.r8
    }
  }, mediaQuery(BreakPoints.Large), {
    borderRadius: CornerRadius.r8
  }),
  cardSkeleton: {
    minHeight: 520
  }
}, {
  name: 'pa-monetization-pricing-cards'
});

function PricingCardsSkeleton() {
  var classes = useStyles$1();
  return /*#__PURE__*/jsxs("div", {
    className: classes.container,
    children: [/*#__PURE__*/jsx(Skeleton, {
      className: classnames(classes.card, classes.cardSkeleton)
    }), /*#__PURE__*/jsx(Skeleton, {
      className: classnames(classes.card, classes.cardSkeleton)
    }), /*#__PURE__*/jsx(Skeleton, {
      className: classnames(classes.card, classes.cardSkeleton)
    })]
  });
}

function ownKeys$2(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$2(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$2(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$2(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var pricingUrl = '/pricing#plans';
var editorUrl = '/create/editor';
var getTierCardsData = function getTierCardsData(isWithPaidTrial) {
  return [{
    id: 'free',
    name: 'Free',
    monthlyPrice: 0,
    currencySymbol: '$',
    href: editorUrl,
    color: BackgroundColors.tint3,
    cardTitle: ['Craft exceptional content'],
    description: ['Selection of free images, videos, and templates to create original content, without breaking the bank.'],
    buttonLabel: ['Get started'],
    buttonProps: {
      variant: ButtonVariants.Outline,
      skin: ButtonSkinAbsoluteBlack
    }
  }, {
    id: 'plus',
    logo: PLUS_LOGO,
    monthlyPrice: 0,
    currencySymbol: '$',
    href: pricingUrl,
    color: BackgroundColors.tint3,
    note: [isWithPaidTrial ? 'Try for {{count}} days. Cancel anytime.' : 'Free for {{count}} days. Cancel anytime.', {
      count: 7
    }],
    cardTitle: ['Elevate your designs'],
    description: ['Access to premium content and templates to level up your designs.'],
    buttonLabel: ['Learn about {{tier}}', {
      tier: 'Plus'
    }],
    buttonProps: {
      variant: ButtonVariants.Outline,
      skin: ButtonSkinAbsoluteBlack
    }
  }, {
    id: 'pro',
    logo: PRO_LOGO,
    monthlyPrice: 0,
    currencySymbol: '$',
    href: pricingUrl,
    color: SecondaryColors.base["default"],
    note: [isWithPaidTrial ? 'Try for {{count}} days. Cancel anytime.' : 'Free for {{count}} days. Cancel anytime.', {
      count: 7
    }],
    cardTitle: ['Maximize creative efficiency'],
    description: ['Full suite of powerful AI tools, with centralized billing and admin controls to scale as your business grows.'],
    buttonLabel: ['Learn about {{tier}}', {
      tier: 'Pro'
    }],
    buttonProps: {
      variant: ButtonVariants.Filled,
      skin: ButtonSkinSecondary
    }
  }];
};
var goldCards = [{
  id: 'free',
  name: 'Free',
  monthlyPrice: 0,
  currencySymbol: '$',
  href: editorUrl,
  color: BackgroundColors.tint3,
  cardTitle: ['Craft exceptional content'],
  description: ['Selection of free images, videos, and templates to create original content, without breaking the bank.'],
  buttonLabel: ['Get started'],
  buttonProps: {
    variant: ButtonVariants.Outline,
    skin: ButtonSkinAbsoluteBlack
  }
}, {
  id: 'gold',
  logo: GOLD_LOGO,
  monthlyPrice: 0,
  currencySymbol: '$',
  href: pricingUrl,
  color: PrimaryColors.base["default"],
  note: ['Free for {{count}} days. Cancel anytime.', {
    count: 7
  }],
  cardTitle: ['Elevate your designs'],
  description: ['Access to the full suite of AI tools and premium content, stickers, fonts, and templates.'],
  buttonLabel: ['Learn about {{tier}}', {
    tier: 'Gold'
  }],
  buttonProps: {
    variant: ButtonVariants.Filled,
    skin: ButtonSkinPrimary
  }
}, {
  id: 'team',
  name: 'Team',
  monthlyPrice: 0,
  currencySymbol: '$',
  href: pricingUrl,
  color: SecondaryColors.base["default"],
  note: ['Free for {{count}} days. Cancel anytime.', {
    count: 7
  }],
  cardTitle: ['Maximize creative efficiency'],
  description: ['Centralized billing and admin controls to scale as your business grows.'],
  buttonLabel: ['Learn about {{tier}}', {
    tier: 'Team'
  }],
  buttonProps: {
    variant: ButtonVariants.Filled,
    skin: ButtonSkinSecondary
  }
}];
var getGoldCards = function getGoldCards(subscriptionPlans, frequency, isMobile) {
  var goldYearlyPlan = subscriptionPlans.find(function (plan) {
    return plan.scope_id === DEFAULT_SCOPE_ID && plan.frequency === frequency;
  });
  var teamYearlyPlan = subscriptionPlans.find(function (plan) {
    return plan.scope_id === DEFAULT_TEAM_SCOPE_ID && plan.frequency === frequency;
  });
  if (!goldYearlyPlan || !teamYearlyPlan) {
    return [];
  }
  return goldCards.map(function (item) {
    var _item$note;
    var currencySymbol = goldYearlyPlan.price.currencySymbol;
    var note = [((_item$note = item.note) === null || _item$note === void 0 ? void 0 : _item$note[0]) || '', {
      count: goldYearlyPlan.trial_period_days
    }];
    var href = item.id === 'free' && isMobile ? STORE_URL : item.href;
    var monthlyPrice = item.id === 'gold' ? toFixedPrice(goldYearlyPlan.price.value / (frequency === 'yearly' ? 12 : 1)) : item.id === 'team' ? toFixedPrice((teamYearlyPlan.price.initial_value || 0) / (frequency === 'yearly' ? 12 : 1)) : 0;
    return _objectSpread$2(_objectSpread$2({}, item), {}, {
      currencySymbol: currencySymbol,
      monthlyPrice: monthlyPrice,
      href: href,
      note: note
    });
  });
};
var getTierCards = function getTierCards(subscriptionPlans, frequency, isMobile) {
  var plusYearlyPlan = subscriptionPlans.find(function (plan) {
    return plan.tier_id === 'plus' && plan.frequency === frequency;
  });
  var proYearlyPlan = subscriptionPlans.find(function (plan) {
    return plan.tier_id === 'pro' && plan.frequency === frequency;
  });
  var isWithPaidTrial = !!(proYearlyPlan !== null && proYearlyPlan !== void 0 && proYearlyPlan.price.intro_offer_value);
  if (!plusYearlyPlan || !proYearlyPlan) {
    return [];
  }
  return getTierCardsData(isWithPaidTrial).map(function (item) {
    var _item$note2;
    var currencySymbol = plusYearlyPlan.price.currencySymbol;
    var note = [((_item$note2 = item.note) === null || _item$note2 === void 0 ? void 0 : _item$note2[0]) || '', {
      count: plusYearlyPlan.trial_period_days
    }];
    var href = item.id === 'free' && isMobile ? STORE_URL : item.href;
    var monthlyPrice = item.id === 'pro' ? toFixedPrice((proYearlyPlan.price.initial_value || 0) / (frequency === 'yearly' ? 12 : 1)) : item.id === 'plus' ? toFixedPrice(plusYearlyPlan.price.value / (frequency === 'yearly' ? 12 : 1)) : 0;
    return _objectSpread$2(_objectSpread$2({}, item), {}, {
      currencySymbol: currencySymbol,
      monthlyPrice: monthlyPrice,
      href: href,
      note: note
    });
  });
};

var useStyles = createUseStyles({
  root: {
    padding: [Spacings.s40, Spacings.s32, Spacings.s72],
    boxShadow: "inset 0 0 0 1px ".concat(BackgroundColors.tint3),
    position: 'relative',
    textAlign: 'center',
    overflow: 'hidden',
    color: TextColors.base,
    '&::before': {
      backgroundColor: function backgroundColor(_ref) {
        var color = _ref.theme.color;
        return color;
      },
      position: 'absolute',
      display: 'block',
      content: '""',
      width: '100%',
      height: 8,
      left: 0,
      top: 0
    }
  },
  name: {
    fontWeight: FontWeights.semibold,
    marginBottom: Spacings.s32,
    fontSize: FontSizes.s24,
    display: 'block',
    lineHeight: 1
  },
  logo: {
    margin: [Spacings.s0, 'auto', Spacings.s32],
    display: 'block',
    width: 'auto',
    height: 24
  },
  price: {
    fontWeight: FontWeights.bold,
    marginBottom: Spacings.s40,
    fontSize: FontSizes.s60,
    lineHeight: 1.2,
    '& small': {
      fontWeight: FontWeights.medium,
      fontSize: FontSizes.s24,
      lineHeight: 1.4
    }
  },
  note: {
    fontWeight: FontWeights.semibold,
    marginTop: Spacings.s16,
    fontSize: FontSizes.s14,
    lineHeight: 1.4,
    minHeight: 40
  },
  separator: {
    margin: [Spacings.s8, Spacings.s0, Spacings.s24],
    backgroundColor: TextColors.tint3,
    height: 1
  },
  title: {
    fontWeight: FontWeights.semibold,
    marginBottom: Spacings.s8,
    fontSize: FontSizes.s20,
    color: PositiveColors.text["default"],
    lineHeight: 1.4
  },
  description: {
    fontWeight: FontWeights.medium,
    fontSize: FontSizes.s16,
    lineHeight: 1.5
  }
}, {
  name: 'pa-monetization-pricing-card'
});

var _excluded = ["currencySymbol", "monthlyPrice", "buttonLabel", "description", "buttonProps", "className", "cardTitle", "color", "note", "name", "logo", "href", "id"];
function ownKeys$1(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function PricingCard(_ref) {
  var _buttonLabel$;
  var _ref$currencySymbol = _ref.currencySymbol,
    currencySymbol = _ref$currencySymbol === void 0 ? '$' : _ref$currencySymbol,
    monthlyPrice = _ref.monthlyPrice,
    buttonLabel = _ref.buttonLabel,
    description = _ref.description,
    buttonProps = _ref.buttonProps,
    className = _ref.className,
    cardTitle = _ref.cardTitle,
    color = _ref.color,
    note = _ref.note,
    name = _ref.name,
    logo = _ref.logo,
    href = _ref.href,
    id = _ref.id,
    rest = _objectWithoutProperties(_ref, _excluded);
  var translate = useTranslator();
  var classes = useStyles({
    theme: {
      color: color
    }
  });
  return /*#__PURE__*/jsxs("div", _objectSpread$1(_objectSpread$1({
    className: classnames(classes.root, className)
  }, rest), {}, {
    children: [!!name && /*#__PURE__*/jsx("strong", {
      className: classes.name,
      children: translate(name)
    }), !!logo && /*#__PURE__*/jsx("img", {
      className: classes.logo,
      src: logo,
      alt: id
    }), /*#__PURE__*/jsxs("div", {
      className: classes.price,
      children: [/*#__PURE__*/jsxs("span", {
        children: [currencySymbol, monthlyPrice]
      }), /*#__PURE__*/jsx("small", {
        children: translate('/mo.')
      })]
    }), /*#__PURE__*/jsx(Button, _objectSpread$1(_objectSpread$1({}, buttonProps), {}, {
      "data-pulse-name": buttonLabel[0].replace('{{tier}}', (buttonLabel === null || buttonLabel === void 0 || (_buttonLabel$ = buttonLabel[1]) === null || _buttonLabel$ === void 0 ? void 0 : _buttonLabel$.tier) || ''),
      tag: ButtonElementTypes.Link,
      target: "_blank",
      href: href,
      isFullWidth: true,
      children: translate.apply(void 0, _toConsumableArray(buttonLabel))
    })), /*#__PURE__*/jsx("div", {
      className: classes.note,
      children: note ? translate.apply(void 0, _toConsumableArray(note)) : ''
    }), /*#__PURE__*/jsx("div", {
      className: classes.separator
    }), /*#__PURE__*/jsx("strong", {
      className: classes.title,
      children: translate.apply(void 0, _toConsumableArray(cardTitle))
    }), /*#__PURE__*/jsx("p", {
      className: classes.description,
      children: translate.apply(void 0, _toConsumableArray(description))
    })]
  }));
}

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function PricingCards(props) {
  var _useState = useState('yearly'),
    _useState2 = _slicedToArray(_useState, 2),
    frequency = _useState2[0],
    setFrequency = _useState2[1];
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    subscriptionPlans = _useState4[0],
    setSubscriptionPlans = _useState4[1];
  var _useState5 = useState(),
    _useState6 = _slicedToArray(_useState5, 2),
    areTiersAvailable = _useState6[0],
    setAreTiersAvailable = _useState6[1];
  var isMobile = !!getMobileOperatingSystem(null);
  var translate = useTranslator();
  var classes = useStyles$1();
  useEffect(function () {
    Promise.all([fetchFeaturesAvailability(['tier']), fetchSubscriptionPlans([DEFAULT_SCOPE_ID, DEFAULT_TEAM_SCOPE_ID, DEFAULT_PLUS_TIER_SCOPE_ID, DEFAULT_PRO_TIER_SCOPE_ID])]).then(function (_ref) {
      var _ref2 = _slicedToArray(_ref, 2),
        featuresResponse = _ref2[0],
        subscriptionPlansResponse = _ref2[1];
      if (featuresResponse.status === 'success') {
        var _featuresResponse$res;
        setAreTiersAvailable((_featuresResponse$res = featuresResponse.response.tier) === null || _featuresResponse$res === void 0 ? void 0 : _featuresResponse$res.is_available);
      }
      if (subscriptionPlansResponse.status === 'success') {
        setSubscriptionPlans(subscriptionPlansResponse.data || []);
      }
    });
  }, []);
  var cards = useMemo(function () {
    if (areTiersAvailable === undefined || !subscriptionPlans.length) {
      return [];
    }
    if (areTiersAvailable) {
      return getTierCards(subscriptionPlans, frequency, isMobile);
    }
    return getGoldCards(subscriptionPlans, frequency, isMobile);
  }, [areTiersAvailable, subscriptionPlans, frequency, isMobile]);
  var handleFrequencyChange = useCallback(function (event) {
    setFrequency(event ? 'yearly' : 'monthly');
  }, []);
  if (!cards.length) {
    return /*#__PURE__*/jsx(PricingCardsSkeleton, {});
  }
  return /*#__PURE__*/jsxs("div", _objectSpread(_objectSpread({
    "data-pulse-section-group": "Pricing Section"
  }, props), {}, {
    children: [/*#__PURE__*/jsx("div", {
      "data-pulse-section": "Year/Month Toggle",
      children: /*#__PURE__*/jsx(Switch, {
        suffixLabel: translate('Yearly (Best value)'),
        prefixLabel: translate('Monthly'),
        onChange: handleFrequencyChange,
        checked: frequency === 'yearly',
        className: classes["switch"]
      })
    }), /*#__PURE__*/jsx("div", {
      className: classes.container,
      children: cards.map(function (data) {
        return /*#__PURE__*/jsx(PricingCard, _objectSpread({
          "data-pulse-section": data.id,
          className: classes.card,
          "data-testid": data.id
        }, data), data.id);
      })
    })]
  }));
}

export { PricingCardsSkeleton, PricingCards as default };
//# sourceMappingURL=index.js.map
